import { ReactNode, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ICONS_WEB } from "@miplatform/constants";
import { appStorage } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Alert, AlertTitle, Collapse, SxProps, Theme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiIconButton } from "../MiIconButton/MiIconButton";

export interface MiAlertProps {
  isOpen?: boolean;
  title?: string;
  description?: string | ReactNode;
  leftIcon?: string;
  iconColor?: string;
  variant?: "success" | "info" | "warning" | "error";
  sx?: SxProps<Theme>;
  storageKey?: string;
  hideCloseButton?: boolean;
  iconSize?: number;
}

export function MiAlert({
  isOpen,
  title,
  description,
  leftIcon,
  iconColor,
  variant = "info",
  sx,
  storageKey,
  hideCloseButton = false,
  iconSize,
}: MiAlertProps) {
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(isOpen || false);
  const { classes, theme } = useStyles();

  useEffect(() => {
    const isVisible = appStorage.getItem(storageKey as string);

    setIsAlertOpen(!isVisible);
  }, [storageKey]);

  useEffect(() => {
    if (typeof isOpen === "boolean") {
      setIsAlertOpen(isOpen);
    }
  }, [isOpen]);

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    if (storageKey) {
      appStorage.setItem(storageKey, "false");
    }
  };

  return (
    <Collapse in={isAlertOpen} className={classes.collapse}>
      <Alert
        className={classes.root}
        sx={{
          backgroundColor: theme.palette[variant].light,
          ...sx,
        }}
        severity={variant}
        icon={
          leftIcon && mdi[leftIcon as keyof typeof mdi] ? (
            <MiIcon
              path={mdi[leftIcon as keyof typeof mdi]}
              color={iconColor || theme.palette.common.black}
              size={iconSize || 1}
            />
          ) : undefined
        }
        action={
          !hideCloseButton && (
            <MiIconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseAlert}
            >
              <MiIcon
                path={mdi[ICONS_WEB.CLOSE as keyof typeof mdi]}
                color={theme.palette.grey.medium}
              />
            </MiIconButton>
          )
        }
      >
        {title && <AlertTitle className={classes.title}>{title}</AlertTitle>}
        {description}
      </Alert>
    </Collapse>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.grey.medium,
    "& .MuiAlert-message": {
      padding: theme.spacing(0.5, 0, 0, 0),
    },
    "& .MuiAlert-action": {
      padding: 0,
    },
  },
  title: {
    color: theme.palette.common.black,
    fontSize: theme.fontSize.medium,
    margin: 0,
  },
  collapse: {
    width: "100%",
  },
}));
